/**
 * ChatMessagesRepo — typed reads over {@link DocStorePort} for
 * `chatMessages/{id}`, the Clubhouse's ONE global ephemeral room.
 *
 * Like {@link CommentsRepo} this repo does NOT author messages: posting is the
 * `postChatMessage` callable's job (it stamps the author's identity from their
 * profile, enforces the rate limit, and sweeps the retention window in the same
 * flow), and clients hold no INSERT grant at all. What lives here is everything
 * that flow needs to READ, plus the sweep itself — the one write, `purgeBefore`,
 * is pure retention and carries no per-message policy.
 *
 * Ephemerality is a property of RETENTION, not of transport: rows past
 * {@link CHAT_RETENTION_MS} are invisible to RLS and physically removed by the
 * sweep. Nothing in this repo is the source of that cut — the migration's read
 * policy is, and the constant below mirrors it.
 */
import { type DocStorePort, type Uid } from '@djgg/core';
import { chatMessageDoc, chatMessagesCollection } from '../paths.js';
import type { ChatMessage } from '../types.js';

/** How long a message stays readable. MIRRORS the 0041 read policy's 24h
 *  window — change one, change both (they are the same rule expressed to two
 *  engines, like the badge evaluator and its SQL view). */
export const CHAT_RETENTION_MS = 24 * 60 * 60 * 1000;

/** How many messages the room shows. The dock is a chat box, not an archive. */
export const CHAT_PAGE_SIZE = 50;

/** Max chat line (chars). A chat line, not a comment essay — deliberately far
 *  below COMMENT_MAX_LEN. Lives here, shared, rather than once in the callable
 *  and once in the composer: `comments` keeps two copies behind a "keep in
 *  sync" comment, and that is precisely the drift the one-path rule forbids. */
export const CHAT_MAX_LEN = 500;

/** Minimum gap between one user's messages. Cheap flood protection for a room
 *  whose whole point is fast back-and-forth: high enough to stop a paste-loop,
 *  low enough that nobody typing normally will ever meet it. Shared so the
 *  composer can explain the limit in the same terms the server enforces it. */
export const CHAT_POST_COOLDOWN_MS = 2_000;

export class ChatMessagesRepo {
  constructor(private readonly store: DocStorePort) {}

  /** One message by id, or null — the ownership check on delete. */
  async get(messageId: string): Promise<ChatMessage | null> {
    return this.store.getDoc<ChatMessage>(chatMessageDoc(messageId));
  }

  /** The newest messages in the room, freshest first. */
  async recent(limit = CHAT_PAGE_SIZE): Promise<Array<ChatMessage & { id: string }>> {
    return this.store.query<ChatMessage & { id: string }>(chatMessagesCollection(), {
      orderBy: [['createdAt', 'desc']],
      limit,
    });
  }

  /** `uid`'s most recent message, or null. Backs the post rate limit — one row,
   *  straight down `chat_messages_uid_created_idx`. */
  async newestByUser(uid: Uid): Promise<(ChatMessage & { id: string }) | null> {
    const rows = await this.store.query<ChatMessage & { id: string }>(chatMessagesCollection(), {
      where: [['uid', '==', uid]],
      orderBy: [['createdAt', 'desc']],
      limit: 1,
    });
    return rows[0] ?? null;
  }

  /** Distinct users who have spoken in the current active room window. */
  async activeParticipantUids(cutoff: number): Promise<Uid[]> {
    const rows = await this.store.query<ChatMessage>(chatMessagesCollection(), {
      where: [['createdAt', '>=', cutoff]],
    });
    return [...new Set(rows.map((row) => row.uid))];
  }

  /**
   * Delete every message older than `cutoff` (epoch-ms), returning how many
   * went. This is the retention sweep — run opportunistically by the post
   * callable rather than on a schedule, so the table cleans itself on the only
   * event that grows it and there is no second moving part to deploy or watch.
   *
   * Bounded per call: a sweep is a side effect of someone saying hi, so it must
   * never turn one post into an unbounded delete. Expired rows are already
   * invisible to readers, so leaving a few for the next post costs nothing.
   */
  async purgeBefore(cutoff: number, limit = CHAT_PAGE_SIZE): Promise<number> {
    const stale = await this.store.query<ChatMessage & { id: string }>(chatMessagesCollection(), {
      where: [['createdAt', '<', cutoff]],
      orderBy: [['createdAt', 'asc']],
      limit,
    });
    if (stale.length === 0) return 0;
    await this.store.batch(
      stale.map((row) => ({ type: 'delete' as const, path: chatMessageDoc(row.id) })),
    );
    return stale.length;
  }
}
